import { dist2, imax, imin, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Enemy, Hero } from '../types.ts';
import type { Pt } from '../caza-types.ts';
import type { ClimbRules, ClimbState } from '../roca-types.ts';
import { ev, spawnEnemy } from './combat.ts';
import { setOpen } from './isla-ice.ts';
import { allyStrike, blowHorn, rocaNpc } from './roca-ally.ts';

/**
 * The climb (docs/sendas/barbaro.md, chapter 5, act 1: «La subida»; the ground in battle/roca-layout.ts): up the ledges
 * of the black rock's wet wall to the cave's mouth, the sea breaking below, the soot crows' nests. Ulla climbs behind
 * the hero and fights (she is the act's: down, it is lost); the pack comes down the ledges for her and the hero, a band
 * onto each ledge as the hero comes onto it and more now and then onto the ledge above; the crows come off the nests at
 * the hero. A howler waits higher up: met, it howls now and then, and if the howl isn't cut half the pack comes down.
 * Ulla's horn (a tap, once) freezes the pack round her a moment. Won at the cave's mouth.
 *
 * Cost: the way's points looked at round the hero every few ticks; one howler; the pack as any band.
 */

/** The horn's tap (the `mark` command's `at`). */
export const ROCA_HORN = 90;

const cr = (s: BattleState): ClimbRules | undefined => s.rules?.senda?.roca?.climb;
const cs = (s: BattleState): ClimbState | undefined => s.senda?.roca?.climb;

const fresh = (s: BattleState, r: ClimbRules): ClimbState => {
  const hw = spawnEnemy(s, 'wolf', 1, false, 0, r.howlerAt.x, r.howlerAt.y, r.howler.hp);
  const w: ClimbState = { far: 0, ledge: 0, next: r.pack.first, crowNext: r.crows.first, howler: { id: hw.id, phase: 0, until: 0, next: 0, hpAt: hw.hp }, howls: 0, cuts: 0, horn: r.horn.uses, scared: 0, ullaCd: 0, band: [], open: 0, done: -1 };
  ramps(s, r, w);
  return w;
};

/** The ramps still held by their ledge's band, shut (the grid's plugs). */
const ramps = (s: BattleState, r: ClimbRules, w: ClimbState): void => setOpen(s, r.ramps.slice(w.open));

/** The climb's howler, if this nightmare is it. */
export const climbHowlerOf = (s: BattleState, id: number): ClimbState['howler'] | undefined => {
  const w = cs(s);
  return w && w.howler.id === id ? w.howler : undefined;
};

/** A point of the way, held on it. */
const wayAt = (r: ClimbRules, i: number): Pt => r.way[imax(0, imin(r.way.length - 1, i))] as Pt;

const pack = (s: BattleState, r: ClimbRules, at: Pt, n: number): number[] => {
  const ids: number[] = [];
  for (let i = 0; i < n; i++) ids.push(spawnEnemy(s, 'wolf', 1, false, 0, at.x + ((i % 3) - 1) * 450, at.y + ((i >> 1) % 2) * 350 - 175, r.wolfHp).id);
  return ids;
};

/** The farthest point of the way the hero stands by (the way is short: all of it looked at). */
const progress = (s: BattleState, r: ClimbRules, w: ClimbState, h: Hero, cue: (c: string) => void): void => {
  let best = w.far;
  let bd = 0x7fffffff;
  for (let i = 0; i < r.way.length; i++) {
    const p = r.way[i] as Pt;
    const d = dist2(p.x, p.y, h.x, h.y);
    if (d < bd) {
      bd = d;
      best = i;
    }
  }
  if (best <= w.far || bd > 1700 * 1700) return;
  w.far = best;
  const k = r.ledgeOf[best] as number;
  while (w.ledge < k) {
    w.ledge++;
    const n = r.ledgeBand[w.ledge] ?? 0;
    // A band comes down onto the new ledge, a little ahead of the hero.
    if (n > 0) {
      const at = wayAt(r, w.far + 6);
      w.band = pack(s, r, at, n);
      ev(s, { t: 'senda', kind: 'rBand', npc: '', x: at.x, y: at.y, n });
      cue(`band${w.ledge}`);
    }
    ev(s, { t: 'senda', kind: 'rLedge', npc: '', x: h.x, y: h.y, n: w.ledge });
    cue(`ledge${w.ledge}`);
  }
};

const howlerTick = (s: BattleState, r: ClimbRules, w: ClimbState, cue: (c: string) => void): void => {
  const H = r.howler;
  const hw = w.howler;
  const e = s.enemies.find((x) => x.id === hw.id);
  if (!e || e.hp <= 0) {
    hw.phase = 4;
    return;
  }
  // Met (the hero a ledge under it): with the pack from now on.
  if (hw.phase === 0) {
    if (w.ledge >= H.at - 1) {
      hw.phase = 1;
      hw.next = s.waveT + H.first;
    }
    return;
  }
  if (hw.phase === 1 && s.waveT >= hw.next && e.stun === 0) {
    hw.phase = 2;
    hw.until = s.tick + H.rear;
    hw.hpAt = e.hp;
    w.howls++;
    ev(s, { t: 'senda', kind: 'rRear', npc: '', x: e.x, y: e.y, n: H.rear });
    cue(`rear${w.howls}`);
  } else if (hw.phase === 2 && s.tick >= hw.until) {
    hw.phase = 3;
    hw.until = s.tick + H.howl;
    ev(s, { t: 'senda', kind: 'rHowl', npc: '', x: e.x, y: e.y, n: H.howl });
  }
  if (hw.phase !== 2 && hw.phase !== 3) return;
  if (hw.hpAt - e.hp >= mulBp(e.maxHp, H.cutBp) || e.stun > 0) {
    hw.phase = 1;
    hw.next = s.waveT + H.every;
    w.cuts++;
    e.stun = imax(e.stun, H.stun);
    ev(s, { t: 'senda', kind: 'rHowlCut', npc: '', x: e.x, y: e.y });
    cue(`cut${w.cuts}`);
  } else if (hw.phase === 3 && s.tick >= hw.until) {
    hw.phase = 1;
    hw.next = s.waveT + H.every;
    const at = wayAt(r, w.far + 7);
    pack(s, r, at, H.raise);
    ev(s, { t: 'senda', kind: 'rHowled', npc: '', x: e.x, y: e.y, n: H.raise });
    cue('howled1');
  }
};

export const climbTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = cr(s);
  const st = s.senda;
  if (!r || !st) return;
  const rs = (st.roca ??= {});
  const w = (rs.climb ??= fresh(s, r));
  if (!s.started || w.done >= 0) return;
  const h = s.heroes.find((x) => x.downTicks === 0);
  // The lowest ledge's band down at once, as it begins: «La manada baja a buscarte».
  if (!w.band.length && w.ledge === 0) {
    w.band = pack(s, r, wayAt(r, 9), r.ledgeBand[0] ?? 0);
    cue('band0');
  }
  if (h && s.tick % 6 === 0) progress(s, r, w, h, cue);
  if (h && withinRange(h.x, h.y, r.mouth.x, r.mouth.y, r.reach)) {
    w.done = s.tick;
    ev(s, { t: 'senda', kind: 'rLedge', npc: '', x: r.mouth.x, y: r.mouth.y, n: r.ledgeBand.length });
    cue('top');
    return;
  }
  howlerTick(s, r, w, cue);
  // The band on the hero's ledge down: its ramp up opens.
  if (w.open <= w.ledge && w.open < r.ramps.length && s.tick % 6 === 0 && !s.enemies.some((e) => e.hp > 0 && w.band.includes(e.id))) {
    w.open = w.ledge + 1;
    ramps(s, r, w);
    const p = wayAt(r, r.ledges[w.open] ?? 0);
    ev(s, { t: 'senda', kind: 'rOpen', npc: '', x: p.x, y: p.y, n: w.ledge });
    cue(`open${w.open}`);
  }
  // The pack on its own, onto the ledge above the hero.
  if (s.waveT >= w.next) {
    w.next = s.waveT + rngRange(s.rng, r.pack.every[0], r.pack.every[1]);
    const at = wayAt(r, w.far + 9);
    pack(s, r, at, rngRange(s.rng, r.pack.n[0], r.pack.n[1]));
  }
  // The crows off the nests above the hero.
  if (s.waveT >= w.crowNext && r.nests.length) {
    w.crowNext = s.waveT + r.crows.every;
    const k = imin(r.nests.length - 1, w.ledge + rngInt(s.rng, 2));
    const p = r.nests[k] as Pt;
    for (let i = 0; i < r.crows.n; i++) spawnEnemy(s, 'crow', 1, false, 0, p.x + (i * 2 - 1) * 400, p.y, r.crowHp);
  }
  w.ullaCd = allyStrike(s, rocaNpc(s, 'ulla'), r.ulla, w.ullaCd);
};

/** The horn (a tap): the pack round Ulla frozen; the howler's howl cut if it hears it. */
export const climbOrder = (s: BattleState, at: number, cue: (c: string) => void): void => {
  const r = cr(s);
  const w = cs(s);
  if (!r || !w || at !== ROCA_HORN || w.horn <= 0 || w.done >= 0) return;
  w.horn--;
  blowHorn(s, rocaNpc(s, 'ulla'), r.horn);
  cue('horn');
};

/** The howler held where it waits, still while it rears and howls (true: the act moved it). */
export const climbHold = (s: BattleState, e: Enemy): boolean => {
  const w = cs(s);
  if (!w || w.howler.id !== e.id) return false;
  const p = w.howler.phase;
  if (p === 0) {
    e.anim = 'idle';
    return true;
  }
  if ((p === 2 || p === 3) && e.stun === 0) {
    e.anim = 'cast';
    return true;
  }
  return false;
};

/** The way climbed, of all of it. */
export const climbGoal = (s: BattleState): { done: number; need: number } => {
  const r = cr(s);
  const w = cs(s);
  const need = imax(1, (r?.way.length ?? 2) - 1);
  return { done: w ? (w.done >= 0 ? need : imin(need, w.far)) : 0, need };
};

export const climbResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = cr(s);
  const w = cs(s);
  return r && w && w.done >= 0 && s.tick >= w.done + r.settle ? 'won' : null;
};
